import React, { useState } from "react";
import { Mail, ArrowUpRight, Linkedin } from "lucide-react";
import Navbar from "../Navbar";
import { Footer } from "../CTAFooter";
import { SITE } from "../../data/mock";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch("/send-contact-email.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Something went wrong. Please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(
        err.message === "Failed to fetch"
          ? `Couldn't reach the server. Please try again, or email ${SITE.email} directly.`
          : err.message
      );
    } finally {
      setSubmitting(false);
    }
  };

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({ url: SITE.calendly });
    } else {
      // Fallback if the widget script hasn't loaded yet (e.g. slow connection) —
      // open Calendly directly in a new tab instead of doing nothing.
      window.open(SITE.calendly, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-neutral-900">
      <Navbar />

      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left: intro + quick contact + talk-live card */}
            <div>
              <span className="inline-block text-[11px] tracking-[0.18em] uppercase text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 bg-white">
                Contact
              </span>
              <h1 className="mt-5 text-[40px] sm:text-[56px] leading-[1.05] tracking-[-0.02em] font-semibold text-neutral-950">
                Get in Touch
              </h1>
              <p className="mt-4 text-[17px] text-neutral-600 max-w-md">
                Have a project in mind or just want to talk growth strategy? Reach out directly, no bots, no forms that go nowhere.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href={`mailto:${SITE.email}`}
                  className="flex items-center gap-2 border border-black/10 rounded-full px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50 transition-colors"
                >
                  <Mail className="w-4 h-4" /> {SITE.email}
                </a>
                <a
                  href={SITE.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 border border-black/10 rounded-full px-4 py-2.5 text-sm text-neutral-800 hover:bg-neutral-50 transition-colors"
                >
                  <Linkedin className="w-4 h-4" /> LinkedIn
                </a>
              </div>

              <div className="mt-12 rounded-3xl bg-neutral-950 p-4 flex items-center gap-4 max-w-md">
                <img
                  src="/assets/abhinavjoshi-square.png"
                  alt="Abhinav Joshi"
                  className="w-16 h-16 rounded-2xl object-cover flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="text-white font-semibold">Let's talk live</div>
                  <div className="text-white/60 text-sm">Book a quick strategy call</div>
                </div>
                <button
                  type="button"
                  onClick={openCalendly}
                  className="bg-white text-black text-sm font-medium px-4 py-2.5 rounded-full hover:bg-neutral-100 transition-colors whitespace-nowrap"
                >
                  Book a Call
                </button>
              </div>
            </div>

            {/* Right: form */}
            <div className="bg-neutral-50 border border-black/5 rounded-3xl p-8">
              <p className="text-neutral-700 mb-6">Tell me what's not working. I'll tell you why.</p>

              {submitted ? (
                <div className="py-16 text-center">
                  <div className="text-lg font-semibold text-neutral-950">Thank you!</div>
                  <p className="mt-2 text-neutral-600">Your message has been received. I'll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="Name"
                    value={form.name}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-2xl border border-black/10 bg-white text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="Email"
                    value={form.email}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-2xl border border-black/10 bg-white text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone (optional)"
                    value={form.phone}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-2xl border border-black/10 bg-white text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                  <input
                    type="text"
                    name="company"
                    placeholder="Company (optional)"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-2xl border border-black/10 bg-white text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/10"
                  />
                  <textarea
                    name="message"
                    required
                    placeholder="Message"
                    rows={6}
                    value={form.message}
                    onChange={handleChange}
                    className="w-full px-5 py-3.5 rounded-2xl border border-black/10 bg-white text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-black/10 resize-none"
                  />
                  {error && (
                    <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-black text-white py-3.5 rounded-2xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : "Submit"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
